/**
 * ErrorSummary — collapsible rose panel listing error entries above the log.
 */

import { useState, useMemo } from 'react';
import { XCircle, AlertTriangle, ChevronDown } from 'lucide-react';
import { cn } from '../../../utils';
import { Text } from '../../atoms/Typography';
import { Button } from '../../atoms/Button';
import { searchConfidenceState, searchDegradedReason } from './searchConfidence';
import { BLOCKED_LABEL_DEFAULTS, FORCED_LABEL_DEFAULTS, forcedEndingNote, isStoppedNode, resolveDelegationState } from './utils';
import type {
  AuditEntry,
  ErrorAuditEntry,
  NodeCompleteAuditEntry,
  ToolResultAuditEntry,
} from './types';

// A run a person stopped is neither an error nor a warning (agion/Agion#2947).
const nodeState = (node: NodeCompleteAuditEntry) => (isStoppedNode(node) ? 'stopped' : resolveDelegationState(node.success, node.outcome));
const isForcedNode = (node: NodeCompleteAuditEntry) => forcedEndingNote(node.success, node.forcedReason) !== undefined;

/** Warning row reasons are truncated to this many characters. */
const WARNING_REASON_MAX_LENGTH = 80;

export interface ErrorSummaryProps {
  entries: AuditEntry[];
  onJumpToEntry: (id: string) => void;
  /** Override labels */
  labels?: Partial<Record<'errorCount' | 'warningCount' | 'rankingDegraded' | 'blocked' | 'forced', string>> & {
    /** Overflow row under each list; receives the number of hidden entries. */
    andMore?: string | ((hiddenCount: number) => string);
  };
}

export function ErrorSummary({ entries, onJumpToEntry, labels }: ErrorSummaryProps) {
  const [expanded, setExpanded] = useState(false);

  const errorEntries = useMemo(() => {
    return entries.filter(e =>
      (e.type === 'error' && (e as ErrorAuditEntry).severity !== 'warning') ||
      (e.type === 'node_complete' && nodeState(e as NodeCompleteAuditEntry) === 'failed') ||
      (e.type === 'tool_result' && !(e as ToolResultAuditEntry).success)
    );
  }, [entries]);

  // Successful tool_result entries whose search confidence degraded (the
  // reranker call failed) — the tool did not error, so these never overlap
  // with errorEntries, but operators still need to see the log wasn't clean.
  // Error entries marked severity 'warning' (an ending to check, not a crash)
  // and nodes the mission setup blocked (agion/Agion#2370) count here instead
  // of as errors, as do completed steps the runner forced to finish
  // (agion/Agion#2460).
  const warningEntries = useMemo(() => {
    return entries.filter(e =>
      (e.type === 'error' && (e as ErrorAuditEntry).severity === 'warning') ||
      (e.type === 'node_complete' && nodeState(e as NodeCompleteAuditEntry) === 'blocked') ||
      (e.type === 'node_complete' && isForcedNode(e as NodeCompleteAuditEntry)) ||
      (e.type === 'tool_result' &&
        (e as ToolResultAuditEntry).success &&
        searchConfidenceState((e as ToolResultAuditEntry).output, (e as ToolResultAuditEntry).toolName) === 'degraded')
    );
  }, [entries]);

  if (errorEntries.length === 0 && warningEntries.length === 0) return null;

  const label = (e: AuditEntry): string => {
    switch (e.type) {
      case 'error':
        return (e as ErrorAuditEntry).message;
      case 'node_complete':
        return `Node failed: ${(e as NodeCompleteAuditEntry).nodeLabel || e.nodeId}`;
      case 'tool_result': {
        const tr = e as ToolResultAuditEntry;
        const name = tr.toolName || 'Tool';
        const err = tr.error || (tr.output && typeof tr.output === 'object' && 'error' in (tr.output as Record<string, unknown>)
          ? ((tr.output as Record<string, unknown>)['error'] as Record<string, unknown>)?.['error_message'] as string ?? 'failed'
          : 'failed');
        return `${name}: ${err}`;
      }
      default:
        return e.type;
    }
  };

  const rankingDegradedLabel = labels?.rankingDegraded ?? 'Ranking degraded';
  const andMore = (hiddenCount: number): string => {
    const custom = labels?.andMore;
    if (typeof custom === 'function') return custom(hiddenCount);
    return custom ?? `+${hiddenCount} more`;
  };

  const warningLabel = (e: AuditEntry): string => {
    if (e.type === 'error') return (e as ErrorAuditEntry).message;
    if (e.type === 'node_complete') {
      const node = e as NodeCompleteAuditEntry;
      const status = isForcedNode(node)
        ? labels?.forced ?? FORCED_LABEL_DEFAULTS.forced
        : labels?.blocked ?? BLOCKED_LABEL_DEFAULTS.blocked;
      return `${status}: ${node.nodeLabel || node.nodeId}`;
    }
    const tr = e as ToolResultAuditEntry;
    const name = tr.toolName || 'Tool';
    const reason = searchDegradedReason(tr.output, tr.toolName)?.slice(0, WARNING_REASON_MAX_LENGTH);
    return reason ? `${name}: ${rankingDegradedLabel} — ${reason}` : `${name}: ${rankingDegradedLabel}`;
  };

  const errorCount = errorEntries.length;
  const warningCount = warningEntries.length;
  const hasErrors = errorCount > 0;
  const hasWarnings = warningCount > 0;
  const onlyWarnings = !hasErrors && hasWarnings;

  const errorCountText = labels?.errorCount ?? `${errorCount} error${errorCount === 1 ? '' : 's'}`;
  const warningCountText = labels?.warningCount ?? `${warningCount} warning${warningCount === 1 ? '' : 's'}`;
  const headerText = hasErrors && hasWarnings
    ? `${errorCountText}, ${warningCountText}`
    : hasErrors
      ? errorCountText
      : warningCountText;

  return (
    <div className={cn(
      'flex-shrink-0 mb-3 rounded-md border',
      onlyWarnings
        ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'
        : 'border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20'
    )}>
      <Button
        variant="ghost"
        className="w-full flex items-center justify-between px-3 py-2 text-left h-auto"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {onlyWarnings ? (
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
          ) : (
            <XCircle className="h-3.5 w-3.5 text-rose-500 flex-shrink-0" />
          )}
          <Text size="xs" weight="medium" className={onlyWarnings ? 'text-amber-700 dark:text-amber-400' : 'text-rose-700 dark:text-rose-400'}>
            {headerText}
          </Text>
        </div>
        <ChevronDown className={cn(
          'h-3.5 w-3.5 transition-transform',
          onlyWarnings ? 'text-amber-400' : 'text-rose-400',
          !expanded && '-rotate-90'
        )} />
      </Button>
      {expanded && (
        <div className="px-3 pb-2 space-y-1">
          {errorEntries.slice(0, 5).map(e => (
            <Button
              key={e.id}
              variant="ghost"
              className="w-full text-left text-xs text-rose-600 dark:text-rose-400 hover:underline truncate block h-auto p-0"
              onClick={() => onJumpToEntry(e.id)}
            >
              {label(e)}
            </Button>
          ))}
          {errorCount > 5 && (
            <Text size="xs" color="muted">
              {andMore(errorCount - 5)}
            </Text>
          )}
          {warningEntries.slice(0, 5).map(e => (
            <Button
              key={e.id}
              variant="ghost"
              className="w-full text-left text-xs text-amber-600 dark:text-amber-400 hover:underline truncate block h-auto p-0"
              onClick={() => onJumpToEntry(e.id)}
            >
              {warningLabel(e)}
            </Button>
          ))}
          {warningCount > 5 && (
            <Text size="xs" color="muted">
              {andMore(warningCount - 5)}
            </Text>
          )}
        </div>
      )}
    </div>
  );
}
